/**
 * POS Cart Context - Manages cart state for POS
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useUpdateHold } from '@/pwa/autoUpdate';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderType, setOrderType] = useState('dine_in'); // dine_in, takeout, delivery
  const [tableNumber, setTableNumber] = useState(null);
  const [customerName, setCustomerName] = useState('');
  // a sale being rung up is never interrupted by an app update (pwa/autoUpdate.js)
  useUpdateHold('pos-cart', cart.length > 0);

  const addItem = useCallback((item, modifiers = [], quantity = 1, notes = '') => {
    const cartItem = {
      id: `${item.id}-${Date.now()}`,
      menuItemId: item.id,
      name: item.name,
      price: item.price,
      quantity,
      modifiers,
      notes,
      modifierTotal: modifiers.reduce((sum, m) => sum + (m.price || 0), 0),
    };
    setCart(prev => [...prev, cartItem]);
    return cartItem;
  }, []);

  const updateQuantity = useCallback((cartItemId, quantity) => {
    if (quantity <= 0) {
      setCart(prev => prev.filter(item => item.id !== cartItemId));
    } else {
      setCart(prev => prev.map(item => 
        item.id === cartItemId ? { ...item, quantity } : item
      ));
    }
  }, []);

  const removeItem = useCallback((cartItemId) => {
    setCart(prev => prev.filter(item => item.id !== cartItemId));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setCurrentOrder(null);
    setTableNumber(null);
    setCustomerName('');
  }, []);

  // The cart's money (subtotal / tax / total) is apps/pos/lib/cartMoney.js + the server's
  // POST /api/checkout/price - never a float sum here (it showed $2.97 for a $2.98 tax).

  const getItemCount = useCallback(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  return (
    <CartContext.Provider value={{
      cart,
      currentOrder,
      orderType,
      tableNumber,
      customerName,
      setCurrentOrder,
      setOrderType,
      setTableNumber,
      setCustomerName,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      getItemCount,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
